import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Spinner, Alert, Card, Row, Col, Pagination } from 'react-bootstrap';
import { FaCalendar, FaDownload, FaRedo } from 'react-icons/fa';
import { getScreeningInfo, downloadScreeningData } from 'store/auth';


const ScreeningTab = () => {
  const [screeningData, setScreeningData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(10);
  
  // Date filter state - default last 10 days
  const getDefaultDates = () => {
    const today = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(today.getDate() - 10);
    
    return {
      from: tenDaysAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0]
    };
  };
  
  const [filters, setFilters] = useState(getDefaultDates());
  const [tempFilters, setTempFilters] = useState(getDefaultDates());

  // Fetch screening data
  const fetchScreeningData = async (page = 1, appliedFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getScreeningInfo({
        page,
        limit,
        startDate: appliedFilters.from || undefined,
        endDate: appliedFilters.to || undefined
      });
      
      setScreeningData(response.data || []);
      setTotalRecords(response.total || 0);
      setCurrentPage(response.page || 1);
      setTotalPages(Math.ceil((response.total || 0) / limit));
      
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to fetch screening data');
      setScreeningData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchScreeningData(1, filters);
  }, [limit]);

  // Handle filter input change
  const handleFilterChange = (field, value) => {
    setTempFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setCurrentPage(1);
    fetchScreeningData(1, tempFilters);
  };

  // Reset filters to default last 10 days
  const handleResetFilters = () => {
    const defaultDates = getDefaultDates();
    setTempFilters(defaultDates);
    setFilters(defaultDates);
    setCurrentPage(1);
    fetchScreeningData(1, defaultDates);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchScreeningData(page, filters);
  };

  // Handle limit change
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  // Handle export all data

  // Handle export filtered data
// Unified CSV Export
const handleExport = async (isFiltered = false) => {
  try {
    setLoading(true);

    if (isFiltered) {
      await downloadScreeningData({
        startDate: filters.from,
        endDate: filters.to,
      });
    } else {
      await downloadScreeningData(); // No params = download all
    }

  } catch (err) {
    setError(err.message || "Failed to download data");
  } finally {
    setLoading(false);
  }
};

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <div>
      {/* Filters Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">Filters</h5>
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold">
                  <FaCalendar className="me-1" />
                  From Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={tempFilters.from}
                  onChange={(e) => handleFilterChange('from', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small fw-semibold">
                  <FaCalendar className="me-1" />
                  To Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={tempFilters.to}
                  onChange={(e) => handleFilterChange('to', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <div className="d-flex align-items-end gap-2 ">
                <Button
                  variant="primary"
                  onClick={handleApplyFilters}
                  className="flex-fill"
                  disabled={loading}
                >
                  Apply
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleResetFilters}
                  disabled={loading}
                >
                  Clear
                </Button>
              </div>
            </Col>
            <Col md={3} className="d-flex align-items-end justify-content-end">
              <Button
                variant="success"
                onClick={() => handleExport(false)}
                disabled={loading}
                className=""
              >
                <FaDownload className="me-2" />
                Export All
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Results Summary */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Screening Records</h5>
          <small className="text-muted">
            Showing {screeningData.length} of {totalRecords} records
          </small>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Form.Group className="d-flex align-items-center mb-0">
            <Form.Label className="mb-0 me-2 small">Rows per page:</Form.Label>
            <Form.Select
              size="sm"
              value={limit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Form.Group>
          <Button
            variant="info"
            onClick={() => handleExport(true)}
            disabled={loading}
          >
            <FaDownload className="me-2" />
            Export 
          </Button>
        </div>
      </div>

      {/* Screening Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading screening data...</p>
            </div>
          ) : screeningData.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
              <p className="text-muted">No screening records found for the selected date range</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-3 py-3">ID</th>
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3">Designation</th>
                    <th className="px-3 py-3">Organisation</th>
                    <th className="px-3 py-3">Organisation Type</th>
                    <th className="px-3 py-3">Email</th>
                    <th className="px-3 py-3">Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {screeningData.map((record) => (
                    <tr key={record.id}>
                      <td className="px-3 py-3">{record.id}</td>
                      <td className="px-3 py-3">
                        <div className="fw-semibold">{record.name || '-'}</div>
                      </td>
                      <td className="px-3 py-3">{record.designation || '-'}</td>
                      <td className="px-3 py-3">{record.organisation || '-'}</td>
                      <td className="px-3 py-3">
                        <span className="px-3 py-3">
                          {record.organisation_type || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-3">{record.email || '-'}</td>
                      <td className="px-3 py-3 text-muted small">
                        {formatDate(record.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {!loading && screeningData.length > 0 && totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-4">
          <Pagination className="mb-0">
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {renderPaginationItems()}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default ScreeningTab;